'use client';

import React, { useState, useEffect, useRef } from 'react';

// ═══ Types ═══
interface GanttTask {
  id: string; title: string; status: string; progress: number;
  assignee: string | null; start: string | null; end: string | null;
  parentId: string | null; projectName: string; color: string;
}

// ═══ P3-06: Gantt Chart Component ═══
export default function GanttChart({ projectId }: { projectId?: string }) {
  const [tasks, setTasks] = useState<GanttTask[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate timeline range
  const today = new Date();
  const startRange = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endRange = new Date(today.getFullYear(), today.getMonth() + 3, 0);
  const totalDays = Math.ceil((endRange.getTime() - startRange.getTime()) / (1000 * 60 * 60 * 24));

  useEffect(() => {
    (async () => {
      try {
        const url = projectId
          ? `/api/flow/tasks/gantt?projectId=${projectId}`
          : '/api/flow/tasks/gantt';
        const res = await fetch(url);
        const data = await res.json();
        setTasks(data.tasks || []);
      } catch {
        // Demo data
        setTasks([
          { id: '1', title: '인테리어 실시설계 도서', status: 'done', progress: 100, assignee: '황지혜', start: '2026-01-23', end: '2026-03-06', parentId: null, projectName: '에코1초', color: '#eab308' },
          { id: '2', title: '평,천 작성', status: 'done', progress: 100, assignee: '황지혜', start: '2026-01-19', end: '2026-01-20', parentId: null, projectName: '에코1초', color: '#eab308' },
          { id: '3', title: '컨셉 및 디자인보고', status: 'waiting', progress: 0, assignee: '기호을', start: '2026-01-05', end: '2026-01-15', parentId: null, projectName: '에코1초', color: '#eab308' },
          { id: '4', title: '김해도서관 가구도 보완', status: 'done', progress: 100, assignee: '황지혜', start: '2026-03-01', end: '2026-03-26', parentId: null, projectName: 'ID_2421_김해도서관', color: '#f97316' },
          { id: '5', title: '대곡고 외부색채작업', status: 'in_progress', progress: 40, assignee: '박현우', start: '2026-03-10', end: '2026-03-25', parentId: null, projectName: 'ID_2531_대곡고', color: '#7c3aed' },
          { id: '6', title: '장안1초 인테리어 도서 작성', status: 'waiting', progress: 0, assignee: '이다운', start: '2026-03-15', end: '2026-04-10', parentId: null, projectName: 'ID_2539 장안1초', color: '#6b7280' },
        ]);
      } finally { setLoading(false); }
    })();
  }, [projectId]);

  // Date helpers
  const dayOffset = (dateStr: string) => {
    const d = new Date(dateStr);
    return Math.max(0, Math.ceil((d.getTime() - startRange.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const months: { label: string; days: number; offset: number }[] = [];
  const d = new Date(startRange);
  while (d <= endRange) {
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    const monthStart = Math.max(0, dayOffset(d.toISOString().split('T')[0]));
    months.push({ label: `${d.getFullYear()}년 ${d.getMonth() + 1}월`, days: daysInMonth, offset: monthStart });
    d.setMonth(d.getMonth() + 1);
    d.setDate(1);
  }

  const statusColors: Record<string, string> = {
    done: '#10b981', in_progress: '#3b82f6', waiting: '#f59e0b', hold: '#6b7280', feedback: '#8b5cf6',
  };

  const dayWidth = 18;
  const todayOffset = dayOffset(today.toISOString().split('T')[0]);

  if (loading) return <div className="p-8 text-center"><div className="animate-spin inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="overflow-hidden rounded-xl" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
      <div className="flex">
        {/* Left: Task list */}
        <div className="w-72 flex-shrink-0" style={{ borderRight: '1px solid var(--border-color)' }}>
          <div className="h-10 px-4 flex items-center" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <span className="text-xs font-medium" style={{ color: 'var(--text-dim)' }}>업무명</span>
          </div>
          {tasks.map(task => (
            <div key={task.id} className="h-10 px-4 flex items-center gap-2 transition-colors" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: task.color }} />
              <span className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{task.title}</span>
            </div>
          ))}
        </div>

        {/* Right: Timeline */}
        <div className="flex-1 overflow-x-auto" ref={containerRef}>
          <div style={{ width: totalDays * dayWidth, minWidth: '100%' }}>
            {/* Month headers */}
            <div className="h-10 flex relative" style={{ borderBottom: '1px solid var(--border-color)' }}>
              {months.map((m, i) => (
                <div key={i} className="absolute top-0 h-full flex items-center px-2"
                  style={{ left: m.offset * dayWidth, width: m.days * dayWidth, borderRight: '1px solid var(--border-color)' }}>
                  <span className="text-xs font-medium whitespace-nowrap" style={{ color: 'var(--text-dim)' }}>{m.label}</span>
                </div>
              ))}
              {/* Today marker */}
              <div className="absolute top-0 h-full w-px bg-red-500/60 z-10" style={{ left: todayOffset * dayWidth }} />
            </div>

            {/* Task bars */}
            {tasks.map(task => {
              if (!task.start || !task.end) return (
                <div key={task.id} className="h-10 relative" style={{ borderBottom: '1px solid var(--border-color)' }} />
              );
              const left = dayOffset(task.start) * dayWidth;
              const width = Math.max(dayWidth, (dayOffset(task.end) - dayOffset(task.start) + 1) * dayWidth);
              const barColor = statusColors[task.status] || '#6b7280';

              return (
                <div key={task.id} className="h-10 relative" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  {/* Today line continues */}
                  <div className="absolute top-0 h-full w-px bg-red-500/20 z-0" style={{ left: todayOffset * dayWidth }} />
                  {/* Task bar */}
                  <div className="absolute top-1.5 h-7 rounded-md flex items-center px-2 cursor-pointer group transition-all hover:brightness-110"
                    style={{ left, width, background: `${barColor}22`, border: `1px solid ${barColor}44` }}>
                    {/* Progress fill */}
                    <div className="absolute inset-0 rounded-md" style={{ width: `${task.progress}%`, background: `${barColor}44` }} />
                    <span className="relative text-[10px] font-medium truncate" style={{ color: barColor }}>
                      {task.assignee || ''} {task.progress > 0 ? `${task.progress}%` : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
